import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useOrgData } from '../hooks/useOrgData';
import { updateStage } from '../lib/data';
import { isAdmin, isAdminOrGestor } from '../lib/rbac';
import { buildTemplateForType } from '../shared/terceirizacao';
import { fmtDate } from '../components/ui';
import { EncaminhamentosPanel } from '../components/EncaminhamentosPanel';
import type { Product, StageStatus, StageTemplate } from '../shared/types';

const today = () => new Date().toISOString().slice(0, 10);

interface Task {
  productId: string;
  productName: string;
  brand: string;
  n: number;
  phase: string;
  stageName: string;
  areaKeys: string[];
  status: StageStatus;
  startedAt: string | null;
  finishedAt: string | null;
  ready: boolean; // não iniciada e todas as dependências concluídas
  pendingDeps: string[]; // nomes das dependências ainda não concluídas
}

function buildTasks(products: Product[], template: StageTemplate[]): Task[] {
  const tasks: Task[] = [];
  for (const p of products) {
    // Projetos cancelados/concluídos não geram tarefas.
    if (p.lifecycle === 'cancelado' || p.lifecycle === 'concluido') continue;
    const tpl = buildTemplateForType(template, p.projectType ?? 'interno');
    const tplByN = new Map(tpl.map((t) => [t.n, t]));
    const statusByN = new Map(p.stages.map((s) => [s.n, s]));
    for (const t of tpl) {
      const inst = statusByN.get(t.n);
      const status = inst?.status ?? 'nao_iniciado';
      const pendingDeps = t.deps
        .filter((d) => (statusByN.get(d)?.status ?? 'nao_iniciado') !== 'concluido')
        .map((d) => tplByN.get(d)?.name ?? `Etapa ${d}`);
      tasks.push({
        productId: p.id,
        productName: p.name,
        brand: p.brand,
        n: t.n,
        phase: t.phase,
        stageName: t.name,
        areaKeys: t.areaKeys,
        status,
        startedAt: inst?.startedAt ?? null,
        finishedAt: inst?.finishedAt ?? null,
        ready: status === 'nao_iniciado' && pendingDeps.length === 0,
        pendingDeps,
      });
    }
  }
  return tasks;
}

export function Tarefas() {
  const { currentOrgId, grants, areas, userId, fullName } = useAuth();
  const { loading, template, products } = useOrgData(currentOrgId);
  const nav = useNavigate();
  const [scope, setScope] = useState<'minhas' | 'todas'>('minhas');
  const [busy, setBusy] = useState<string | null>(null);

  const areaLabel = useMemo(() => {
    const m = new Map(areas.map((a) => [a.key, a.name]));
    return (k: string) => m.get(k) ?? k;
  }, [areas]);

  const pmo = grants ? isAdminOrGestor(grants) : false;

  const tasks = useMemo(() => {
    const all = buildTasks(products, template);
    const mine =
      pmo || scope === 'todas'
        ? all
        : all.filter((t) => t.areaKeys.some((a) => grants?.areaKeys.includes(a)));
    return mine;
  }, [products, template, grants, pmo, scope]);

  const emAndamento = tasks.filter((t) => t.status === 'em_andamento');
  const prontas = tasks.filter((t) => t.ready);
  const bloqueadas = tasks.filter((t) => t.status === 'nao_iniciado' && !t.ready);
  const concluidas = tasks
    .filter((t) => t.status === 'concluido')
    .sort((a, b) => (b.finishedAt ?? '').localeCompare(a.finishedAt ?? ''));

  const canAct = grants ? grants.role !== 'viewer' : false;

  async function act(t: Task, next: StageStatus) {
    setBusy(`${t.productId}-${t.n}`);
    const patch: Parameters<typeof updateStage>[2] =
      next === 'concluido'
        ? { status: 'concluido', finished_at: today(), started_at: t.startedAt ?? today() }
        : { status: 'em_andamento', started_at: t.startedAt ?? today() };
    const { error } = await updateStage(t.productId, t.n, patch);
    if (error) alert(error);
    setBusy(null);
  }

  function Item({ t, kind }: { t: Task; kind: 'ready' | 'wip' | 'blocked' | 'done' }) {
    const key = `${t.productId}-${t.n}`;
    return (
      <div className="task-row">
        <div className="task-main">
          <div className="task-title">
            <span className="tag-mini">{t.phase}</span> {t.stageName}
          </div>
          <div className="muted task-sub">
            <button className="linklike" onClick={() => nav(`/produto/${t.productId}`)}>
              {t.brand} · {t.productName}
            </button>
            {' · '}
            {t.areaKeys.map(areaLabel).join(' / ')}
          </div>
          {kind === 'blocked' && (
            <div className="task-block">Aguardando: {t.pendingDeps.join(', ')}</div>
          )}
        </div>
        <div className="task-actions">
          {kind === 'ready' && canAct && (
            <>
              <button className="btn-sm" disabled={busy === key} onClick={() => act(t, 'em_andamento')}>
                Iniciar
              </button>
              <button className="btn-primary btn-sm" disabled={busy === key} onClick={() => act(t, 'concluido')}>
                Concluir
              </button>
            </>
          )}
          {kind === 'wip' && canAct && (
            <button className="btn-primary btn-sm" disabled={busy === key} onClick={() => act(t, 'concluido')}>
              Concluir
            </button>
          )}
          {kind === 'blocked' && <span className="seal pend">Bloqueada</span>}
          {kind === 'done' && (
            <span className="seal ok">
              ✓ {t.finishedAt ? `Concluída em ${fmtDate(t.finishedAt)}` : 'Concluída'}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="topbar">
        <h1>Minhas tarefas</h1>
        <div className="stat-strip">
          <div className="stat-chip">
            <span className="n tabular">{prontas.length}</span>
            <span className="l">Prontas</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{emAndamento.length}</span>
            <span className="l">Em andamento</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{bloqueadas.length}</span>
            <span className="l">Bloqueadas</span>
          </div>
        </div>
      </div>

      <div className="content">
        <EncaminhamentosPanel
          userId={userId}
          fullName={fullName}
          isAdmin={grants ? isAdmin(grants) : false}
          products={products}
          template={template}
        />
        {!pmo && grants?.role === 'editor' && (
          <div className="chip-toggle" style={{ marginBottom: 14 }}>
            <button className={scope === 'minhas' ? 'on' : ''} onClick={() => setScope('minhas')}>
              Minhas áreas
            </button>
            <button className={scope === 'todas' ? 'on' : ''} onClick={() => setScope('todas')}>
              Todas as áreas
            </button>
          </div>
        )}

        {loading ? (
          <div className="center-note">Carregando…</div>
        ) : (
          <>
            <Section title="Prontas para começar" count={prontas.length} hint="Dependências concluídas — pode iniciar.">
              {prontas.map((t) => <Item key={`${t.productId}-${t.n}`} t={t} kind="ready" />)}
            </Section>
            <Section title="Em andamento" count={emAndamento.length}>
              {emAndamento.map((t) => <Item key={`${t.productId}-${t.n}`} t={t} kind="wip" />)}
            </Section>
            <Section title="Bloqueadas" count={bloqueadas.length} hint="Aguardando etapas anteriores.">
              {bloqueadas.map((t) => <Item key={`${t.productId}-${t.n}`} t={t} kind="blocked" />)}
            </Section>
            <Section
              title="Concluídas"
              count={concluidas.length}
              hint="Etapas já finalizadas — recolhidas por padrão."
              collapsible
              defaultCollapsed
            >
              {concluidas.map((t) => <Item key={`${t.productId}-${t.n}`} t={t} kind="done" />)}
            </Section>
          </>
        )}
      </div>
    </>
  );
}

function Section({
  title, count, hint, children, collapsible, defaultCollapsed,
}: {
  title: string;
  count: number;
  hint?: string;
  children: ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const showBody = !collapsible || open;
  return (
    <div className="card card-pad" style={{ marginBottom: 14 }}>
      <div className="section-title">
        <h2>
          {collapsible ? (
            <button className="linklike sec-toggle" onClick={() => setOpen((o) => !o)}>
              <span className="exp-caret">{open ? '▾' : '▸'}</span> {title}{' '}
              <span className="muted">({count})</span>
            </button>
          ) : (
            <>
              {title} <span className="muted">({count})</span>
            </>
          )}
        </h2>
        {hint && showBody && <span className="muted" style={{ fontSize: 12 }}>{hint}</span>}
      </div>
      {showBody &&
        (count === 0 ? <p className="muted">Nada aqui.</p> : <div className="task-list">{children}</div>)}
    </div>
  );
}
